import { format, parse } from "date-fns";

export const OPCIONES_FORMA_PAGO_CREDITO = [
  "diario",
  "semanal",
  "mensual",
] as const;

export type FormaDePagoCredito = (typeof OPCIONES_FORMA_PAGO_CREDITO)[number];

const DIAS_POR_FORMA_PAGO: Record<FormaDePagoCredito, number> = {
  diario: 1,
  mensual: 30,
  semanal: 7,
};

export const normalizarNumeroSeguro = (
  value: number | string | null | undefined
): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const normalizarFormaDePago = (
  formaDePago: string | null | undefined
): FormaDePagoCredito => {
  if (formaDePago === "semanal" || formaDePago === "mensual") {
    return formaDePago;
  }

  return "diario";
};

const parsearFechaBase = (fechaBase: string | Date): Date => {
  if (fechaBase instanceof Date) {
    return new Date(fechaBase);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(fechaBase)) {
    return parse(fechaBase, "yyyy-MM-dd", new Date());
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(fechaBase)) {
    return parse(fechaBase, "dd/MM/yyyy", new Date());
  }

  const parsedDate = new Date(fechaBase);

  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

export const formatearFechaActualDisplay = (): string =>
  format(new Date(), "dd/MM/yyyy");

export const formatearFechaActualIso = (): string =>
  format(new Date(), "yyyy-MM-dd");

export const formatearFechaDisplayDesdeIso = (fechaIso: string): string => {
  const [year = "", month = "", day = ""] = fechaIso.split("-");

  if (!year || !month || !day) {
    return "";
  }

  return `${day}/${month}/${year}`;
};

export const calcularPagoDiarioCredito = (
  valorPrestamo: number | string,
  porcentajeInteres: number | string,
  cuotasPactadas: number | string
): number => {
  const valorPrestamoNormalizado = normalizarNumeroSeguro(valorPrestamo);
  const porcentajeInteresNormalizado =
    normalizarNumeroSeguro(porcentajeInteres);
  const cuotasPactadasNormalizadas = normalizarNumeroSeguro(cuotasPactadas);

  if (cuotasPactadasNormalizadas <= 0) {
    return 0;
  }

  const valorTotal =
    valorPrestamoNormalizado +
    (valorPrestamoNormalizado * porcentajeInteresNormalizado) / 100;

  return valorTotal / cuotasPactadasNormalizadas;
};

export const calcularFechaFinalCredito = ({
  avanzarUnDiaInicial = true,
  cuotasPactadas,
  fechaBase,
  formaDePago,
}: {
  avanzarUnDiaInicial?: boolean;
  cuotasPactadas: number | string;
  fechaBase: string | Date;
  formaDePago: string | null | undefined;
}): string => {
  const cuotasPactadasNormalizadas = normalizarNumeroSeguro(cuotasPactadas);

  if (cuotasPactadasNormalizadas <= 0) {
    return "";
  }

  const fechaInicial = parsearFechaBase(fechaBase);
  const fechaFinal = new Date(fechaInicial);
  const formaDePagoNormalizada = normalizarFormaDePago(formaDePago);

  if (avanzarUnDiaInicial) {
    fechaFinal.setDate(fechaFinal.getDate() + 1);
  }

  for (let index = 0; index < cuotasPactadasNormalizadas; index += 1) {
    fechaFinal.setDate(
      fechaFinal.getDate() + DIAS_POR_FORMA_PAGO[formaDePagoNormalizada]
    );

    if (fechaFinal.getDay() === 0) {
      fechaFinal.setDate(fechaFinal.getDate() + 1);
    }
  }

  return format(fechaFinal, "dd/MM/yyyy");
};

export const leerArchivoComoPreview = (
  file: File,
  onLoad: (result: string | ArrayBuffer | null) => void
): void => {
  const reader = new FileReader();
  reader.onloadend = () => {
    onLoad(reader.result);
  };
  reader.readAsDataURL(file);
};
